/**
 * Content Type Registration for Web Content App
 *
 * Register all available content types from content-elements and content-types
 * This mirrors the editor app's content-types.ts
 */

import {
    defineContentType,
    registerContentType,
    getRegistry,
    type ContentTypeDefinition,
} from '@ai-content-hub/content-core';

// Import elements from content-elements
import {
    // Text Element
    textElementSchema,
    TextEdit,
    TextPlayer,
    // Image Element
    imageElementSchema,
    ImageEdit,
    ImagePlayer,
    // Rich Text Element
    richTextElementSchema,
    RichTextEdit,
    RichTextPlayer,
    // Audio Element
    audioElementSchema,
    AudioEdit,
    AudioPlayer,
    // Video Element
    videoElementSchema,
    VideoEdit,
    VideoPlayer,
    // Media Element (unified)
    mediaElementSchema,
    MediaEdit,
    MediaPlayer,
    // MCQ Element
    mcqElementSchema,
    MCQEdit,
    MCQPlayer,
    mcqGrader,
    type MCQElementData,
    type MCQAnswer,
    // Match Element
    matchGrader,
    type MatchElementData,
    type MatchAnswer,
    // Color Drop Zone Element
    colorDropZoneGrader,
    type ColorDropZoneElementData,
    type ColorDropZoneAnswer,
    // Page Element
    pageElementSchema,
    PageEdit,
    PagePlayer,
    fitbElementSchema,
    FITBEdit,
    FITBPlayer,
    fitbGrader,
    defaultFITBConfig,
    type FITBElementData,
    type FITBAnswer,
    // Ordering Element
    orderingElementSchema,
    OrderingEdit,
    OrderingPlayer,
    orderingGrader,
    type OrderingElementData,
    type OrderingAnswer,
} from '@ai-content-hub/content-elements';

// Import composite content types
import {
    mcqSetSchema,
    McqSetEdit,
    MCQSetPlayer,
    defaultMCQSetConfig,
    mcqSetContentGrader,
    VERSION as mcqSetVersion,
} from '@ai-content-hub/content-type-mcq-set';
import {
    questionSetSchema,
    QuestionSetEdit,
    QuestionSetPlayer,
    defaultQuestionSetConfig,
    questionSetContentGrader,
    questionSetValidator,
    VERSION as questionSetVersion,
} from '@ai-content-hub/content-type-question-set';
import {
    qtDropdownSchema,
    DropdownEdit,
    DropdownPlayer,
    defaultQtDropdownConfig,
    qtDropdownContentGrader,
    validateQtDropdownContent,
    VERSION as qtDropdownVersion,
    type QtDropdownData,
} from '@ai-content-hub/content-type-dropdown';
import {
    qtDragTheWordsSchema,
    DragTheWordsEdit,
    DragTheWordsPlayer,
    defaultQtDragTheWordsConfig,
    qtDragTheWordsContentGrader,
    validateQtDragTheWordsContent,
    VERSION as qtDragTheWordsVersion,
    type QtDragTheWordsData,
} from '@ai-content-hub/content-type-drag-the-words';
import {
    speakingSchema,
    SpeakingEdit,
    SpeakingPlayer,
    defaultSpeakingConfig,
    speakingContentGrader,
    speakingValidator,
    VERSION as speakingVersion,
} from '@ai-content-hub/content-type-speaking';
// EAA-1755: interactive-reading (TTS reading + synced word highlighting + pronunciation grading).
import {
    interactiveReadingSchema,
    InteractiveReadingEdit,
    InteractiveReadingPlayer,
    interactiveReadingContentGrader,
    interactiveReadingValidator,
    defaultInteractiveReadingConfig,
    VERSION as interactiveReadingVersion,
} from '@ai-content-hub/content-type-interactive-reading';
import {
    quizSchema,
    QuizPlayer,
    QuizEdit,
    quizContentGrader,
    quizValidator,
    VERSION as quizVersion,
} from '@ai-content-hub/content-type-quiz';
import {
    lessonMCQSchema,
    LessonMCQEdit,
    LessonMCQPlayer,
    lessonMCQValidator,
    VERSION as lessonMcqVersion,
    type LessonMCQData,
} from '@ai-content-hub/content-type-lesson-mcq';
import {
    richMatchingSchema,
    RichMatchingEdit,
    RichMatchingPlayer,
    richMatchingValidator,
    type RichMatchingData,
    VERSION as richMatchingVersion,
} from '@ai-content-hub/content-type-rich-matching';
import {
    imageHotspotSchema,
    ImageHotspotEdit,
    ImageHotspotPlayer,
    imageHotspotValidator,
    type ImageHotspotData,
    VERSION as imageHotspotVersion,
} from '@ai-content-hub/content-type-image-hotspot';
import {
    essaySchema,
    EssayEdit,
    EssayPlayer,
    essayContentGrader,
    essayValidator,
    VERSION as essayVersion,
} from '@ai-content-hub/content-type-essay';
import {
    documentsSchema,
    DocumentsEdit,
    DocumentsPlayer,
    documentsContentGrader,
    documentsValidator,
    VERSION as documentsVersion,
} from '@ai-content-hub/content-type-documents';
// EAA-1642: 3D model content type (GLB/glTF viewer via three.js).
import {
    content3DSchema,
    Content3DEdit,
    Content3DPlayer,
    content3DContentGrader,
    content3DValidator,
    VERSION as content3DVersion,
} from '@ai-content-hub/content-type-3d';
import {
    orderingContentSchema,
    OrderingContentEdit,
    OrderingContentPlayer,
    type OrderingContentData,
    gradeOrderingContentComposite,
    validateOrderingContentComposite,
    VERSION as orderingContentVersion,
} from '@ai-content-hub/content-type-ordering';
import {
    fitbContentSchema,
    FITBContentEdit,
    FITBContentPlayer,
    type FITBContentData,
    gradeFITBContentComposite,
    validateFITBContentComposite,
    VERSION as fitbContentVersion,
} from '@ai-content-hub/content-type-fill-in-the-blanks';
import {
    ScormEdit,
    ScormPlayer,
    defaultScormData,
} from '@ai-content-hub/scorm';

/** Content-type package versions, keyed by type slug. */
export const contentTypeVersions: Record<string, string> = {
    'mcq-set': mcqSetVersion,
    'question-set': questionSetVersion,
    'qt-dropdown': qtDropdownVersion,
    'qt-drag-the-words': qtDragTheWordsVersion,
    speaking: speakingVersion,
    'interactive-reading': interactiveReadingVersion,
    quiz: quizVersion,
    'lesson-mcq': lessonMcqVersion,
    'rich-matching': richMatchingVersion,
    'image-hotspot': imageHotspotVersion,
    essay: essayVersion,
    documents: documentsVersion,
    '3d': content3DVersion,
    'ordering-content': orderingContentVersion,
    'fill-in-the-blanks-content': fitbContentVersion,

};

// ============================================
// Define Element Content Types
// ============================================

export const textContentType = defineContentType({
    type: 'text',
    title: textElementSchema.title,
    description: textElementSchema.description,
    version: textElementSchema.version,
    category: 'text',
    fields: textElementSchema.fields as never[],
    defaultData: textElementSchema.defaultData,
    EditComponent: TextEdit as never,
    PlayerComponent: TextPlayer as never,
});

export const imageContentType = defineContentType({
    type: 'image',
    title: imageElementSchema.title,
    description: imageElementSchema.description,
    version: imageElementSchema.version,
    category: 'media',
    fields: imageElementSchema.fields as never[],
    defaultData: imageElementSchema.defaultData,
    EditComponent: ImageEdit as never,
    PlayerComponent: ImagePlayer as never,
});

export const richTextContentType = defineContentType({
    type: 'rich-text',
    title: richTextElementSchema.title,
    description: richTextElementSchema.description,
    version: richTextElementSchema.version,
    category: 'text',
    fields: richTextElementSchema.fields as never[],
    defaultData: richTextElementSchema.defaultData,
    EditComponent: RichTextEdit as never,
    PlayerComponent: RichTextPlayer as never,
});

export const audioContentType = defineContentType({
    type: 'audio',
    title: audioElementSchema.title,
    description: audioElementSchema.description,
    version: audioElementSchema.version,
    category: 'media',
    fields: audioElementSchema.fields as never[],
    defaultData: audioElementSchema.defaultData,
    EditComponent: AudioEdit as never,
    PlayerComponent: AudioPlayer as never,
});

export const videoContentType = defineContentType({
    type: 'video',
    title: videoElementSchema.title,
    description: videoElementSchema.description,
    version: videoElementSchema.version,
    category: 'media',
    fields: videoElementSchema.fields as never[],
    defaultData: videoElementSchema.defaultData,
    EditComponent: VideoEdit as never,
    PlayerComponent: VideoPlayer as never,
});

export const mediaContentType = defineContentType({
    type: 'media',
    title: mediaElementSchema.title,
    description: mediaElementSchema.description,
    version: mediaElementSchema.version,
    category: 'media',
    fields: mediaElementSchema.fields as never[],
    defaultData: mediaElementSchema.defaultData,
    EditComponent: MediaEdit as never,
    PlayerComponent: MediaPlayer as never,
});

export const mcqContentType = defineContentType({
    type: 'mcq',
    title: mcqElementSchema.title,
    description: mcqElementSchema.description,
    version: mcqElementSchema.version,
    category: 'question',
    fields: mcqElementSchema.fields as never[],
    defaultData: mcqElementSchema.defaultData,
    grader: {
        grade: (data: unknown, answer: unknown) => {
            return mcqGrader.grade(data as MCQElementData, answer as MCQAnswer);
        },
    },
    EditComponent: MCQEdit as never,
    PlayerComponent: MCQPlayer as never,
});

export const pageContentType = defineContentType({
    type: 'page',
    title: pageElementSchema.title,
    description: pageElementSchema.description,
    version: pageElementSchema.version,
    category: 'layout' as never,
    fields: pageElementSchema.fields as never[],
    defaultData: pageElementSchema.defaultData,
    EditComponent: PageEdit as never,
    PlayerComponent: PagePlayer as never,
});

export const fillInBlanksContentType = defineContentType({
    type: 'fill-in-the-blanks',
    title: fitbElementSchema.title,
    description: fitbElementSchema.description,
    version: fitbElementSchema.version,
    category: 'question',
    fields: fitbElementSchema.fields as never[],
    defaultData: fitbElementSchema.defaultData,
    defaultConfig: defaultFITBConfig,
    grader: {
        grade: (data: unknown, answer: unknown, config?: Record<string, unknown>) => {
            return fitbGrader.grade(data as FITBElementData, answer as FITBAnswer, config);
        },
    },
    validator: {
        validate: (data: unknown) => fitbGrader.validate!(data as FITBElementData),
    },
    EditComponent: FITBEdit as never,
    PlayerComponent: FITBPlayer as never,
    hasIntegratedConfigTab: true,
    showEditorErrorSummary: false,
});

export const orderingContentType = defineContentType({
    type: 'ordering',
    title: orderingElementSchema.title,
    description: orderingElementSchema.description,
    version: orderingElementSchema.version,
    category: 'question',
    fields: orderingElementSchema.fields as never[],
    defaultData: orderingElementSchema.defaultData,
    grader: {
        grade: (data: unknown, answer: unknown, config?: Record<string, unknown>) => {
            return orderingGrader.grade(
                data as OrderingElementData,
                answer as OrderingAnswer,
                config
            );
        },
    },
    EditComponent: OrderingEdit as never,
    PlayerComponent: OrderingPlayer as never,
});

// ============================================
// Define Composite Content Types
// ============================================

export const mcqSetContentType = defineContentType({
    type: 'mcq-set',
    title: mcqSetSchema.title,
    description: mcqSetSchema.description,
    version: mcqSetSchema.version,
    category: 'assessment' as never,
    fields: [],
    defaultData: mcqSetSchema.defaultData,
    defaultConfig: defaultMCQSetConfig,
    EditComponent: McqSetEdit as never,
    PlayerComponent: MCQSetPlayer as never,
    hasIntegratedConfigTab: true,
    grader: mcqSetContentGrader as never,
});

export const questionSetContentType = defineContentType({
    type: 'question-set',
    title: questionSetSchema.title,
    description: questionSetSchema.description,
    version: questionSetSchema.version,
    category: 'assessment' as never,
    fields: [],
    defaultData: questionSetSchema.defaultData,
    defaultConfig: defaultQuestionSetConfig,
    EditComponent: QuestionSetEdit as never,
    PlayerComponent: QuestionSetPlayer as never,
    hasIntegratedConfigTab: true,
    grader: questionSetContentGrader as never,
    validator: questionSetValidator,
    showEditorErrorSummary: false,
});

export const qtDropdownContentType = defineContentType({
    type: 'qt-dropdown',
    title: qtDropdownSchema.title,
    description: qtDropdownSchema.description,
    version: qtDropdownSchema.version,
    category: 'assessment' as never,
    fields: [],
    defaultData: qtDropdownSchema.defaultData,
    defaultConfig: defaultQtDropdownConfig,
    EditComponent: DropdownEdit as never,
    PlayerComponent: DropdownPlayer as never,
    hasIntegratedConfigTab: true,
    showEditorErrorSummary: false,
    grader: qtDropdownContentGrader as never,
    validator: {
        validate: (data: unknown) => validateQtDropdownContent(data as QtDropdownData),
    },
});

export const qtDragTheWordsContentType = defineContentType({
    type: 'qt-drag-the-words',
    title: qtDragTheWordsSchema.title,
    description: qtDragTheWordsSchema.description,
    version: qtDragTheWordsSchema.version,
    category: 'assessment' as never,
    fields: [],
    defaultData: qtDragTheWordsSchema.defaultData,
    defaultConfig: defaultQtDragTheWordsConfig,
    EditComponent: DragTheWordsEdit as never,
    PlayerComponent: DragTheWordsPlayer as never,
    hasIntegratedConfigTab: true,
    showEditorErrorSummary: false,
    grader: qtDragTheWordsContentGrader as never,
    validator: {
        validate: (data: unknown) => validateQtDragTheWordsContent(data as QtDragTheWordsData),
    },
});

export const speakingContentType = defineContentType({
    type: 'speaking',
    title: speakingSchema.title,
    description: speakingSchema.description,
    version: speakingSchema.version,
    category: 'assessment' as never,
    fields: [],
    defaultData: speakingSchema.defaultData,
    defaultConfig: defaultSpeakingConfig,
    EditComponent: SpeakingEdit as never,
    PlayerComponent: SpeakingPlayer as never,
    hasIntegratedConfigTab: true,
    grader: speakingContentGrader as never,
    validator: speakingValidator,
    showEditorErrorSummary: false,
});

// EAA-1755: Interactive Reading — TTS passage + synced word highlighting + pronunciation grading.
export const interactiveReadingContentType = defineContentType({
    type: 'interactive-reading',
    title: interactiveReadingSchema.title,
    description: interactiveReadingSchema.description,
    version: interactiveReadingSchema.version,
    category: 'assessment' as never,
    fields: [],
    defaultData: interactiveReadingSchema.defaultData,
    defaultConfig: defaultInteractiveReadingConfig,
    EditComponent: InteractiveReadingEdit as never,
    PlayerComponent: InteractiveReadingPlayer as never,
    grader: interactiveReadingContentGrader as never,
    validator: interactiveReadingValidator,
    showEditorErrorSummary: false,
});

export const quizContentType = defineContentType({
    type: 'quiz',
    title: quizSchema.title,
    description: quizSchema.description,
    version: quizSchema.version,
    category: 'assessment' as never,
    fields: [],
    defaultData: quizSchema.defaultData,
    EditComponent: QuizEdit as never,
    PlayerComponent: QuizPlayer as never,
    grader: quizContentGrader as never,
    validator: quizValidator,
    showEditorErrorSummary: false,
});

export const lessonMCQContentType = defineContentType({
    type: 'lesson-mcq',
    title: lessonMCQSchema.title,
    description: lessonMCQSchema.description,
    version: lessonMCQSchema.version,
    category: 'assessment' as never,
    fields: [],
    defaultData: lessonMCQSchema.defaultData,
    defaultConfig: lessonMCQSchema.defaultConfig,
    EditComponent: LessonMCQEdit as never,
    PlayerComponent: LessonMCQPlayer as never,
    hasIntegratedConfigTab: true,
    grader: {
        grade: (data, answer, scoring, ctx) =>
            mcqSetContentGrader.grade(
                (data as LessonMCQData).questionSet,
                answer as Record<string, MCQAnswer | undefined>,
                scoring,
                ctx
            ),
        validateAnswer: (answer, data, opts) =>
            mcqSetContentGrader.validateAnswer!(
                answer as Record<string, MCQAnswer | undefined>,
                (data as LessonMCQData).questionSet,
                opts
            ),
    } as never,
    validator: lessonMCQValidator,
    showEditorErrorSummary: false,
});

// Rich Matching - Lesson content with matching questions
export const richMatchingContentType = defineContentType({
    type: 'rich-matching',
    title: richMatchingSchema.title,
    description: richMatchingSchema.description,
    version: richMatchingSchema.version,
    category: 'assessment' as never,
    fields: [],
    defaultData: richMatchingSchema.defaultData,
    defaultConfig: richMatchingSchema.defaultConfig,
    grader: {
        grade: (data: unknown, answer: unknown, config?: Record<string, unknown>) => {
            const richData = data as RichMatchingData;
            return matchGrader.grade(
                richData.matchData as MatchElementData,
                answer as MatchAnswer,
                config
            );
        },
    },
    EditComponent: RichMatchingEdit as never,
    PlayerComponent: RichMatchingPlayer as never,
    hasIntegratedConfigTab: true,
    validator: richMatchingValidator,
    showEditorErrorSummary: false,
});

// Image Hotspot - Image with clickable hotspots that reveal content
export const imageHotspotContentType = defineContentType({
    type: 'image-hotspot',
    title: imageHotspotSchema.title,
    description: imageHotspotSchema.description,
    version: imageHotspotSchema.version,
    category: 'interactive' as never,
    fields: [],
    defaultData: imageHotspotSchema.defaultData,
    defaultConfig: imageHotspotSchema.defaultConfig,
    grader: {
        grade: (data: unknown, answer: unknown, config?: Record<string, unknown>) => {
            const hotspotData = data as ImageHotspotData;
            return colorDropZoneGrader.grade(
                hotspotData.colorDropZoneData as ColorDropZoneElementData,
                answer as ColorDropZoneAnswer,
                config
            );
        },
    },
    EditComponent: ImageHotspotEdit as never,
    PlayerComponent: ImageHotspotPlayer as never,
    hasIntegratedConfigTab: true,
    validator: imageHotspotValidator,
    showEditorErrorSummary: false,
});

// Essay - Lesson content with essay writing questions
export const essayContentType = defineContentType({
    type: 'essay',
    title: essaySchema.title,
    description: essaySchema.description,
    version: essaySchema.version,
    category: 'assessment' as never,
    fields: [],
    defaultData: essaySchema.defaultData,
    defaultConfig: essaySchema.defaultConfig,
    EditComponent: EssayEdit as never,
    PlayerComponent: EssayPlayer as never,
    grader: essayContentGrader as never,
    hasIntegratedConfigTab: true,
    validator: essayValidator,
    showEditorErrorSummary: false,
});

// Documents (Tài liệu) — Office/PDF viewer + completion gates
export const documentsContentType = defineContentType({
    type: 'documents',
    title: documentsSchema.title,
    description: documentsSchema.description,
    version: documentsSchema.version,
    category: documentsSchema.category as never,
    fields: [],
    defaultData: documentsSchema.defaultData,
    defaultConfig: documentsSchema.defaultConfig,
    validator: documentsValidator,
    EditComponent: DocumentsEdit as never,
    PlayerComponent: DocumentsPlayer as never,
    grader: documentsContentGrader as never,
    hasIntegratedConfigTab: true,
    inlinePlayerSubmit: true,
    showEditorErrorSummary: false,
});

// 3D Model — GLB/glTF viewer rendered with three.js (EAA-1642)
export const content3DContentType = defineContentType({
    type: '3d',
    title: content3DSchema.title,
    description: content3DSchema.description,
    version: content3DSchema.version,
    category: content3DSchema.category as never,
    fields: [],
    defaultData: content3DSchema.defaultData,
    defaultConfig: content3DSchema.defaultConfig,
    validator: content3DValidator,
    EditComponent: Content3DEdit as never,
    PlayerComponent: Content3DPlayer as never,
    grader: content3DContentGrader as never,
    hasIntegratedConfigTab: true,
    inlinePlayerSubmit: true,
    showEditorErrorSummary: false,
});

// SCORM — Unified SCORM content type (auto-detects 1.2 / 2004 from package)
export const scormContentType = defineContentType({
    type: 'scorm',
    title: 'SCORM',
    description: 'Interactive SCORM learning content package (supports 1.2 & 2004)',
    version: '1.0.0',
    category: 'layout' as never,
    fields: [],
    defaultData: defaultScormData,
    EditComponent: ScormEdit as never,
    PlayerComponent: ScormPlayer as never,
    hasIntegratedConfigTab: true,
    showEditorErrorSummary: false,
});
// ============================================
// All Content Types
// ============================================

export const elementTypes: ContentTypeDefinition[] = [
    textContentType as never,
    imageContentType as never,
    richTextContentType as never,
    audioContentType as never,
    videoContentType as never,
    mediaContentType as never,
    mcqContentType as never,
    pageContentType as never,
    fillInBlanksContentType as never,
    orderingContentType as never,
];

// Ordering Content Type — wraps ordering element with title/description
export const orderingContentTypeComposite = defineContentType({
    type: 'ordering-content',
    title: orderingContentSchema.title,
    description: orderingContentSchema.description,
    version: orderingContentSchema.version,
    category: 'assessment' as never,
    fields: [],
    defaultData: orderingContentSchema.defaultData,
    defaultConfig: orderingContentSchema.defaultConfig,
    grader: {
        grade: (data: unknown, answer: unknown, config?: Record<string, unknown>) => {
            return gradeOrderingContentComposite(
                data as OrderingContentData,
                answer,
                config,
            );
        },
    },
    validator: {
        validate: (data: unknown) => validateOrderingContentComposite(data as OrderingContentData),
    },
    EditComponent: OrderingContentEdit as never,
    PlayerComponent: OrderingContentPlayer as never,
    hasIntegratedConfigTab: true,
    showEditorErrorSummary: false,
});

// Fill in the Blanks Content Type — wraps FITB element with title/description
export const fitbContentTypeComposite = defineContentType({
    type: 'fill-in-the-blanks-content',
    title: fitbContentSchema.title,
    description: fitbContentSchema.description,
    version: fitbContentSchema.version,
    category: 'assessment' as never,
    fields: [],
    defaultData: fitbContentSchema.defaultData,
    defaultConfig: fitbContentSchema.defaultConfig,
    grader: {
        grade: (data: unknown, answer: unknown, config?: Record<string, unknown>) => {
            return gradeFITBContentComposite(data as FITBContentData, answer, config);
        },
    },
    validator: {
        validate: (data: unknown) => validateFITBContentComposite(data as FITBContentData),
    },
    EditComponent: FITBContentEdit as never,
    PlayerComponent: FITBContentPlayer as never,
    hasIntegratedConfigTab: true,
    showEditorErrorSummary: false,
});

export const compositeTypes: ContentTypeDefinition[] = [
    mcqSetContentType as never,
    questionSetContentType as never,
    qtDropdownContentType as never,
    qtDragTheWordsContentType as never,
    speakingContentType as never,
    interactiveReadingContentType as never,
    quizContentType as never,
    lessonMCQContentType as never,
    richMatchingContentType as never,
    imageHotspotContentType as never,
    essayContentType as never,
    documentsContentType as never,
    content3DContentType as never,
    orderingContentTypeComposite as never,
    fitbContentTypeComposite as never,
];

export const scormTypes: ContentTypeDefinition[] = [
    scormContentType as never,
];

export const contentTypes: ContentTypeDefinition[] = [...elementTypes, ...compositeTypes, ...scormTypes];

// ============================================
// Register All Content Types
// ============================================

let registered = false;

export function registerAllContentTypes(): void {
    if (registered) return;

    contentTypes.forEach((type) => {
        registerContentType(type);
    });

    registered = true;
    console.log(
        `[content-core] Registered ${contentTypes.length} content types (${elementTypes.length} elements + ${compositeTypes.length} composites)`
    );
}

// ============================================
// Get Content Type Info
// ============================================

export function getContentTypeList() {
    return contentTypes.map((type) => ({
        type: type.schema.type,
        title: type.schema.title,
        description: type.schema.description,
        category: type.schema.category,
    }));
}

export function getContentType(type: string) {
    return getRegistry().get(type);
}
